// Version 1.2
// Fixed unbinding of resize event
// Added ability to disable the code
//
// Version 1.1a
//
// closeButton added for 1.1a
//
// author: Jonthan Dallas
// date: November 2020
//
// pass these props when intializing this module
//
// props: {
//   animationDelay: number
//   closed: boolean
//   reversed: boolean
//   direction: string (horizontal or vertical)
//   menuButton: string.isRequired (class or id selector)
//   closeButton: string.isRequired (class or id selector)
//   menuContainer: string.isRequired (class or id selector)
//   navigationLink: string.isRequired (class selector)
//   openClass: string (class name)
//   onClose: function (callback when the menu opens)
//   onOpen: function (callback when the menu opens)
//   parent: node_element.isRequired
// }

import uniqueid from "lodash.uniqueid";

let timeoutId = window.setTimeout(() => {}, 0);

export default function (initProps) {
  let propsFail = false;

  if (!initProps.menuButton || initProps.menuButton.length === 0) {
    console.error("'menuButton' selector is required");
    propsFail = true;
  }
  if (!initProps.menuContainer || initProps.menuContainer.length === 0) {
    console.error("'menuContainer' selector is required");
    propsFail = true;
  }
  if (
    (!initProps.navigationLink || initProps.navigationLink.length === 0)
  ) {
    console.error("'navigationLink' selector is required");
    propsFail = true;
  }
  if (!initProps.parent) {
    console.error("'parent' node element is required");
    propsFail = true;
  }

  if (propsFail) {
    return;
  }

  const defaultProps = {
    animationDelay: 600,
    closed: true,
    direction: "vertical",
    reversed: false,
    openClass: "is-open",
  };

  const props = Object.assign({}, defaultProps, initProps);
  
  // current state
  let closedState = props.closed;
  let currentWidth = window.innerWidth;
  let direction = props.direction;
  
  const animationDelay = props.animationDelay;
  const elParent = props.parent;

  const elMenuButton = elParent.querySelector(props.menuButton);
  const elCloseButton = elParent.querySelector(props.closeButton);
  const elMenuContainer = elParent.querySelector(props.menuContainer);
  const elMenuLinks = elMenuContainer.querySelectorAll(props.navigationLink);

  init();

  function init() {
    bindEvents();

    const controlId = elMenuContainer.id || uniqueid("menu");
    const buttonId = elMenuButton.id || uniqueid("menu");

    elMenuButton.id = buttonId;
    elMenuButton.setAttribute("aria-haspopup", true);
    elMenuButton.setAttribute("aria-controls", controlId);

    elMenuContainer.id = controlId;
    elMenuContainer.setAttribute("aria-labelledby", buttonId);

    if (closedState) {
      closeMenu(false);
    } else {
      openMenu(false);
    }
  }

  function kill() {
    // reset state
    let closedState = props.closed;
    let currentWidth = window.innerWidth;
    let direction = props.direction;
  
    elMenuButton.removeAttribute("aria-haspopup");
    elMenuButton.removeAttribute("aria-controls");
    elMenuButton.removeAttribute("aria-expanded");
    elMenuContainer.removeAttribute("aria-labelledby");
    elMenuContainer.removeAttribute("aria-hidden");

    unbindEvents()
  }

  function bindEvents() {
    // need to run this on mousedown to properly allow the focus state to move.
    if (elCloseButton) {
      elCloseButton.addEventListener("mousedown", handleCloseMenu);
      elCloseButton.addEventListener("keydown", handleMenuKeyEvent);
    }

    // need to run this on mousedown to properly allow the focus state to move.
    elMenuButton.addEventListener("mousedown", handleToggleMenu);
    elMenuButton.addEventListener("keydown", handleMenuKeyEvent);

    [...elMenuLinks].forEach((link, index) => {
      link.data = {link, index};
      link.addEventListener("keydown", handleLinkKeyEvent)

      if (link.tagName === "A") {
        link.addEventListener("click", handleCloseMenu);
      }
    });
  }

  function unbindEvents() {
    // need to run this on mousedown to properly allow the focus state to move.
    if (elCloseButton) {
      elCloseButton.removeEventListener("mousedown", handleCloseMenu);
      elCloseButton.removeEventListener("keydown", handleMenuKeyEvent);
    }

    // need to run this on mousedown to properly allow the focus state to move.
    elMenuButton.removeEventListener("mousedown", handleToggleMenu);
    elMenuButton.removeEventListener("keydown", handleMenuKeyEvent);

    [...elMenuLinks].forEach((link, index) => {
      link.data = {link, index};
      link.removeEventListener("keydown", handleLinkKeyEvent)

      if (link.tagName === "A") {
        link.removeEventListener("click", handleCloseMenu);
      }
    });
  }

  function setDirection(newDirection) {
    direction = newDirection;
  }

  function clickAway(e) {
    if (!elParent.contains(e.target) && !closedState) {
      closeMenu(false);
    }
  }

  function handleToggleMenu(e) {
    e.preventDefault();
    toggleMenu();
  }

  function toggleMenu(moveFocus) {
    if (closedState) {
      openMenu(moveFocus);
    } else {
      closeMenu(moveFocus);
    }
  }

  function openMenu(moveFocus = true) {
    closedState = false;
    currentWidth = window.innerWidth;

    elMenuButton.setAttribute("aria-expanded", true);
    elParent.classList.add(props.openClass);
    elMenuContainer.removeAttribute("aria-hidden");

    if (moveFocus) {
      window.clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        elMenuLinks[0] && elMenuLinks[0].focus();
      }, animationDelay);
    }

    if (props.onOpen) {
      props.onOpen();
    }

    window.addEventListener("click", clickAway);
    window.addEventListener("resize", resizeClose);
  }

  function resizeClose() {
    if (currentWidth !== window.innerWidth) {
      currentWidth = window.innerWidth;
      closeMenu();
    }
  }

  function handleCloseMenu(e) {
    e.preventDefault();
    closeMenu(e.target === elCloseButton);
  }

  function closeMenu(moveFocus = true) {
    closedState = true;
    if (moveFocus) {
      elMenuButton && elMenuButton.focus();
    }
    elMenuButton.setAttribute("aria-expanded", false);
    elParent.classList.remove(props.openClass);
    elMenuContainer.setAttribute("aria-hidden", true);

    if (props.onClose) {
      props.onClose();
    }

    window.removeEventListener("click", clickAway);
    window.removeEventListener("resize", resizeClose);
  }

  function handleMenuKeyEvent(e) {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeMenu();
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        toggleMenu(true);
        break;
      default:
    }
  }

  function handleLinkKeyEvent(e, link, index) {
    let target = index;

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeMenu(true);
        break;
      case "Tab":
        if (e.shiftKey) {
          if (index === 0) {
            if (!props.reversed) {
              e.preventDefault();
            }
            closeMenu(!props.reversed);
          }
        } else {
          if (index === elMenuLinks.length - 1) {
            if (props.reversed) {
              e.preventDefault();
            }
            closeMenu(props.reversed);
          }
        }
        break;
      default:
    }

    if (direction === "vertical") {
      switch (e.key) {
        case "Up":
        case "ArrowUp":
          e.preventDefault();
          focusPrevious(elMenuLinks, index, target);
          break;
        case "Down":
        case "ArrowDown":
          e.preventDefault();
          focusNext(elMenuLinks, index, target);
          break;
        default:
      }
    }
    if (direction === "horizontal") {
      switch (e.key) {
        case "Left":
        case "ArrowLeft":
          e.preventDefault();
          focusPrevious(elMenuLinks, index, target);
          break;
        case "Right":
        case "ArrowRight":
          e.preventDefault();
          focusNext(elMenuLinks, index, target);
          break;
        default:
      }
    }

    function focusPrevious(elList, index, target) {
      if (index === 0) {
        target = elMenuLinks.length;
      }
      elMenuLinks[target - 1] && elMenuLinks[target - 1].focus();
    }

    function focusNext(elList, index, target) {
      if (index === elMenuLinks.length - 1) {
        target = -1;
      }
      elMenuLinks[target + 1] && elMenuLinks[target + 1].focus();
    }
  }

  return {
    init,
    kill,
    toggleMenu,
    openMenu,
    closeMenu,
    setDirection,
  };
}
